import { useState } from 'react'
import { tournamentApi } from '../services/api'
import { useLanguage } from '../context/LanguageContext'
import { mdToHtml } from './MarkdownField'
import { localizedTerms } from '../config/termsContent'

/**
 * Blocking Terms of Use acceptance modal.
 *
 * Rendered by ResellerShell when the account owner has outstanding documents
 * to accept. `pending` is an array of { kind: 'platform'|'parent', version }.
 * Non-dismissible: the owner must check every document and accept. One POST
 * to /terms/accept records acceptance for every currently-outstanding doc
 * (server-side), per-document, with IP + user-agent.
 */
export default function TermsAcceptModal({ pending = [], onAccepted }) {
  const { t, langId } = useLanguage()
  const [checked, setChecked] = useState({}) // versionId -> bool
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  const allChecked = pending.length > 0 && pending.every(d => checked[d.version.id])

  function toggle(id) {
    setChecked(c => ({ ...c, [id]: !c[id] }))
  }

  async function accept() {
    setSaving(true); setErr(null)
    try {
      await tournamentApi.post('/portal/reseller/terms/accept', {})
      onAccepted?.()
    } catch (e) {
      setErr(e?.response?.data?.message || e.message)
    } finally { setSaving(false) }
  }

  function docHeading(doc) {
    const { title } = localizedTerms(doc.version, langId)
    if (doc.kind === 'platform') return t('sp_terms_doc_integolf', 'InteGolf Terms of Use')
    return title || t('sp_terms_doc_partner', 'Partner Terms of Use')
  }

  return (
    <div style={overlay}>
      <div style={card}>
        <style>{`
          .terms-modal-body { color: #334155; font-size: 14px; line-height: 1.6; }
          .terms-modal-body h2 { font-size: 16px; color: #0f172a; margin: 18px 0 8px; }
          .terms-modal-body p { margin: 0 0 12px; }
          .terms-modal-body ul { margin: 0 0 12px; padding-left: 20px; }
          .terms-modal-body li { margin: 0 0 5px; }
          .terms-modal-body strong { color: #0f172a; }
        `}</style>

        <div style={{ padding: '24px 28px 8px' }}>
          <h2 style={{ margin: 0, fontSize: 20, color: '#0f172a' }}>{t('sp_terms_accept_title', 'Please review and accept our Terms of Use')}</h2>
          <p style={{ margin: '8px 0 0', fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
            {pending.length > 1
              ? t('sp_terms_accept_intro_multi', 'To continue, please read and accept the following documents.')
              : t('sp_terms_accept_intro', 'To continue using your reseller portal, please read and accept the current Terms of Use.')}
          </p>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 28px 8px' }}>
          {pending.map(doc => {
            const { content, version: vlabel } = { ...localizedTerms(doc.version, langId), version: doc.version.version }
            const html = mdToHtml(content)
            return (
              <div key={doc.version.id} style={docCard}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                  <strong style={{ fontSize: 15, color: '#0f172a' }}>{docHeading(doc)}</strong>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>{t('sp_terms_version_label', 'Version')} {vlabel}</span>
                </div>
                <div className="terms-modal-body" style={docScroll} dangerouslySetInnerHTML={{ __html: html }} />
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 14, color: '#0f172a', marginTop: 10 }}>
                  <input type="checkbox" checked={!!checked[doc.version.id]} onChange={() => toggle(doc.version.id)} style={{ marginTop: 3 }} />
                  <span>{t('sp_terms_accept_checkbox', 'I have read and agree to the Terms of Use.')}</span>
                </label>
              </div>
            )
          })}
        </div>

        {err && <div style={errorBox}>{err}</div>}

        <div style={{ padding: '14px 28px 24px', borderTop: '1px solid #e2e8f0' }}>
          <button
            type="button"
            onClick={accept}
            disabled={!allChecked || saving}
            style={{ ...acceptBtn, opacity: (!allChecked || saving) ? 0.5 : 1, cursor: (!allChecked || saving) ? 'not-allowed' : 'pointer' }}
          >
            {saving ? t('sp_sending_ellipsis', 'Sending…') : t('sp_terms_accept_btn', 'Accept and continue')}
          </button>
        </div>
      </div>
    </div>
  )
}

const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 4000, padding: 20 }
const card = { background: '#fff', borderRadius: 12, width: '100%', maxWidth: 720, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }
const docCard = { border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px', marginBottom: 14 }
const docScroll = { maxHeight: 240, overflowY: 'auto', marginTop: 8, paddingRight: 6, borderTop: '1px solid #f1f5f9' }
const errorBox = { margin: '0 28px', padding: 10, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, fontSize: 13 }
const acceptBtn = { width: '100%', padding: '12px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600 }
